/**
 * Search History Store (Zustand)
 *
 * Manages search history for both anonymous and authenticated users.
 * - Anonymous: in-memory session history (lost on page refresh)
 * - Authenticated: persisted to Firestore (max 50 items)
 * - On login: session items migrate to Firestore automatically
 * - Deduplication (same symbol updates timestamp)
 * - Collapsible sidebar state
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  doc,
  getDoc,
  updateDoc,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';

// Search history item interface
export interface SearchHistoryItem {
  id: string;
  symbol: string;
  name: string;
  timestamp: Date;
  type: 'stock' | 'portfolio';
}

// Firestore format (with Timestamp instead of Date)
interface FirestoreSearchHistoryItem {
  id: string;
  symbol: string;
  name: string;
  timestamp: Timestamp;
  type: 'stock' | 'portfolio';
}

// Maximum number of history items to keep
const MAX_HISTORY_ITEMS = 50;
const MAX_SESSION_ITEMS = 20;

interface SearchHistoryState {
  // State
  history: SearchHistoryItem[];           // Firestore-backed (auth'd users)
  sessionHistory: SearchHistoryItem[];    // In-memory only (anonymous users)
  isLoading: boolean;
  isSidebarOpen: boolean;
  error: string | null;

  // Actions
  loadHistory: (userId: string) => Promise<void>;
  addSearch: (
    userId: string | null,
    item: { symbol: string; name: string; type: 'stock' | 'portfolio' }
  ) => Promise<void>;
  removeItem: (userId: string, itemId: string) => Promise<void>;
  clearHistory: (userId: string) => Promise<void>;
  migrateSessionToFirestore: (userId: string) => Promise<void>;
  clearSession: () => void;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
}

// Generate a unique ID for history items
const generateId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

export const useSearchHistoryStore = create<SearchHistoryState>()(
  devtools(
    (set, get) => ({
      // Initial state
      history: [],
      sessionHistory: [],
      isLoading: false,
      isSidebarOpen: false,
      error: null,

      // Load history from Firestore
      loadHistory: async (userId: string) => {
        if (!userId) {
          set({ history: [], error: null });
          return;
        }

        set({ isLoading: true, error: null });

        try {
          const userDocRef = doc(db, 'users', userId);
          const userDoc = await getDoc(userDocRef);

          if (userDoc.exists()) {
            const data = userDoc.data();
            const firestoreHistory: FirestoreSearchHistoryItem[] = data.searchHistory || [];

            // Convert Firestore Timestamps to Dates and sort descending
            const history: SearchHistoryItem[] = firestoreHistory
              .map((item) => ({
                ...item,
                timestamp: item.timestamp?.toDate?.() || (item.timestamp ? new Date(item.timestamp as any) : new Date()),
              }))
              .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
              .slice(0, MAX_HISTORY_ITEMS);

            set({ history, isLoading: false });
          } else {
            set({ history: [], isLoading: false });
          }
        } catch {
          set({ error: 'Failed to load search history', isLoading: false });
        }
      },

      // Add a new search to history (supports null userId for anonymous users)
      addSearch: async (userId, { symbol, name, type }) => {
        if (!symbol) return;

        const normalizedSymbol = symbol.toUpperCase().trim();

        // Create new item
        const newItem: SearchHistoryItem = {
          id: generateId(),
          symbol: normalizedSymbol,
          name: name || normalizedSymbol,
          timestamp: new Date(),
          type,
        };

        // Anonymous user: store in session memory only
        if (!userId) {
          const currentSession = get().sessionHistory;
          const filteredSession = currentSession.filter(
            (item) => item.symbol !== normalizedSymbol
          );
          const updatedSession = [newItem, ...filteredSession].slice(0, MAX_SESSION_ITEMS);
          set({ sessionHistory: updatedSession });
          return;
        }

        // Authenticated user: optimistic update + Firestore sync
        const currentHistory = get().history;

        // Remove existing entry for same symbol (deduplication)
        const filteredHistory = currentHistory.filter(
          (item) => item.symbol !== normalizedSymbol
        );

        // Add new item at the beginning and limit to MAX_HISTORY_ITEMS
        const updatedHistory = [newItem, ...filteredHistory].slice(0, MAX_HISTORY_ITEMS);
        set({ history: updatedHistory });

        // Sync to Firestore
        try {
          const userDocRef = doc(db, 'users', userId);
          const userDoc = await getDoc(userDocRef);

          if (userDoc.exists()) {
            const data = userDoc.data();
            const firestoreHistory: FirestoreSearchHistoryItem[] = data.searchHistory || [];

            // Remove existing entry for same symbol
            const filteredFirestoreHistory = firestoreHistory.filter(
              (item) => item.symbol !== normalizedSymbol
            );

            // Create Firestore-compatible item
            const firestoreItem: FirestoreSearchHistoryItem = {
              id: newItem.id,
              symbol: newItem.symbol,
              name: newItem.name,
              timestamp: Timestamp.fromDate(newItem.timestamp),
              type: newItem.type,
            };

            // Add new item and limit to MAX_HISTORY_ITEMS
            const newFirestoreHistory = [firestoreItem, ...filteredFirestoreHistory].slice(
              0,
              MAX_HISTORY_ITEMS
            );

            await updateDoc(userDocRef, {
              searchHistory: newFirestoreHistory,
            });
          }
        } catch {
          // Revert optimistic update on error
          set({ history: currentHistory });
        }
      },

      // Remove a specific item from history
      removeItem: async (userId, itemId) => {
        if (!userId || !itemId) return;

        const currentHistory = get().history;
        const updatedHistory = currentHistory.filter((item) => item.id !== itemId);

        // Optimistic update
        set({ history: updatedHistory });

        try {
          const userDocRef = doc(db, 'users', userId);
          const userDoc = await getDoc(userDocRef);

          if (userDoc.exists()) {
            const data = userDoc.data();
            const firestoreHistory: FirestoreSearchHistoryItem[] = data.searchHistory || [];
            const newFirestoreHistory = firestoreHistory.filter((item) => item.id !== itemId);

            await updateDoc(userDocRef, {
              searchHistory: newFirestoreHistory,
            });
          }
        } catch {
          // Revert optimistic update
          set({ history: currentHistory });
        }
      },

      // Clear all history
      clearHistory: async (userId) => {
        if (!userId) return;

        const currentHistory = get().history;

        // Optimistic update
        set({ history: [] });

        try {
          const userDocRef = doc(db, 'users', userId);
          await updateDoc(userDocRef, {
            searchHistory: [],
          });
        } catch {
          // Revert optimistic update
          set({ history: currentHistory });
        }
      },

      // Migrate session history to Firestore on login
      migrateSessionToFirestore: async (userId: string) => {
        const { sessionHistory, addSearch } = get();
        if (!userId || sessionHistory.length === 0) return;

        // Migrate oldest-first so newest ends up on top after dedup
        const itemsToMigrate = [...sessionHistory].reverse();
        for (const item of itemsToMigrate) {
          await addSearch(userId, { symbol: item.symbol, name: item.name, type: item.type });
        }

        set({ sessionHistory: [] });
      },

      // Clear session history (e.g., on logout)
      clearSession: () => {
        set({ sessionHistory: [] });
      },

      // Toggle sidebar visibility
      toggleSidebar: () => {
        set((state) => ({ isSidebarOpen: !state.isSidebarOpen }));
      },

      // Set sidebar visibility directly
      setSidebarOpen: (open) => {
        set({ isSidebarOpen: open });
      },
    }),
    { name: 'search-history-store' }
  )
);
